import type { LatLngTuple } from './geo';

// A patrol's recorded route is made of legs. A slot is the gap between two
// consecutive QR checkpoints, plus one closing slot from the last back to the
// first; a leg is a path guards walked that fills a slot. Checkpoints without
// a QR code that sit inside a slot are passed along the way.
//
// The database decides which legs exist and are still valid
// (get_patrol_route_legs). These helpers only lay them out for display.

export interface PatrolRouteCheckpoint {
  checkpointId: string;
  order: number;
  qrRequired: boolean;
  lat: number;
  lng: number;
}

export interface PatrolRouteLeg {
  fromCheckpointId: string;
  toCheckpointId: string;
  positions: LatLngTuple[];
}

export interface PatrolRouteSlot {
  fromCheckpointId: string;
  toCheckpointId: string;
  isClosing: boolean;
  /** The walked path, or null when none has been recorded for this slot. */
  recorded: LatLngTuple[] | null;
  /** Straight lines through the slot's checkpoints. */
  straight: LatLngTuple[];
}

export interface PatrolRouteCoverage {
  /** Slots between consecutive QR checkpoints, not counting the closing one. */
  total: number;
  /** How many of those have a recorded leg. */
  recorded: number;
  recordingEnabled: boolean;
}

/**
 * Lays a patrol out as slots. Returns none when the patrol has fewer than two
 * QR checkpoints, or uses one twice: such a patrol has no recorded route.
 */
export function buildPatrolRouteSlots(
  checkpoints: PatrolRouteCheckpoint[],
  legs: PatrolRouteLeg[] = [],
): PatrolRouteSlot[] {
  const ordered = [...checkpoints].sort((left, right) => left.order - right.order);
  const required = ordered.filter((checkpoint) => checkpoint.qrRequired);
  if (required.length < 2) return [];
  if (new Set(required.map((checkpoint) => checkpoint.checkpointId)).size !== required.length) {
    return [];
  }
  const waypoints = ordered.filter((checkpoint) => !checkpoint.qrRequired);

  return required.map((from, index) => {
    const to = required[(index + 1) % required.length];
    const isClosing = index === required.length - 1;
    // The closing slot wraps: waypoints after the last QR checkpoint come
    // before those ahead of the first.
    const via = isClosing
      ? [
        ...waypoints.filter((checkpoint) => checkpoint.order > from.order),
        ...waypoints.filter((checkpoint) => checkpoint.order < to.order),
      ]
      : waypoints.filter((checkpoint) => checkpoint.order > from.order && checkpoint.order < to.order);
    const leg = legs.find((candidate) => (
      candidate.fromCheckpointId === from.checkpointId
      && candidate.toCheckpointId === to.checkpointId
      && candidate.positions.length >= 2
    ));

    return {
      fromCheckpointId: from.checkpointId,
      toCheckpointId: to.checkpointId,
      isClosing,
      recorded: leg ? leg.positions : null,
      straight: [from, ...via, to].map((checkpoint): LatLngTuple => [checkpoint.lat, checkpoint.lng]),
    };
  });
}

/**
 * Splits slots into the lines a preview draws: walked paths, and straight
 * lines for slots with none. The closing slot is drawn straight only when the
 * patrol is marked as a loop, but its walked path is always shown.
 */
export function splitPatrolRoutePreview(
  slots: PatrolRouteSlot[],
  isLoop: boolean,
): { recorded: LatLngTuple[][]; straight: LatLngTuple[][] } {
  const recorded: LatLngTuple[][] = [];
  const straight: LatLngTuple[][] = [];
  for (const slot of slots) {
    if (slot.recorded) recorded.push(slot.recorded);
    else if (!slot.isClosing || isLoop) straight.push(slot.straight);
  }
  return { recorded, straight };
}

/**
 * Counts, per patrol, how many of its slots have a recorded leg. `legRows`
 * must be what get_patrol_route_legs returned, which is already limited to
 * legs that fit the patrol as it is defined now.
 */
export function summarisePatrolRouteCoverage(
  patrolIds: string[],
  requiredRows: Array<{ patrol_id: string }>,
  legRows: Array<{ patrol_id: string; is_closing: boolean }>,
  settingsRows: Array<{ patrol_id: string; recording_enabled: boolean }>,
): Record<string, PatrolRouteCoverage> {
  const coverage: Record<string, PatrolRouteCoverage> = {};
  for (const patrolId of patrolIds) {
    const requiredCount = requiredRows.filter((row) => row.patrol_id === patrolId).length;
    const total = Math.max(requiredCount - 1, 0);
    const recorded = legRows.filter((row) => row.patrol_id === patrolId && !row.is_closing).length;
    const settings = settingsRows.find((row) => row.patrol_id === patrolId);
    coverage[patrolId] = {
      total,
      recorded: Math.min(recorded, total),
      recordingEnabled: settings ? settings.recording_enabled : true,
    };
  }
  return coverage;
}
